import React from "react";
import {
  AbsoluteFill,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

export const FreshLiquidChrome: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 1. Slow, High-End Luxury Drift (Anti-Slideshow)
  const cameraDrift = interpolate(frame, [0, 180], [1.0, 1.04]);
  const floatY = Math.sin(frame / 25) * 12;

  // 2. Liquid Chrome Specular Sheen Sweep
  const sheenX = interpolate(frame, [15, 65], [-120, 220], {
    easing: Easing.bezier(0.16, 1, 0.3, 1),
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // 3. Card Entrance (Smooth heavy spring)
  const cardSpring = spring({
    frame,
    fps,
    config: { damping: 22, stiffness: 120, mass: 1.2 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#05070B",
        color: "#FFFFFF",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'SF Pro Display', sans-serif",
        overflow: "hidden",
        transform: `scale(${cameraDrift})`,
      }}
    >
      {/* 1. Deep Caustic Ambient Background Lights */}
      <div
        style={{
          position: "absolute",
          top: "-10%",
          left: "20%",
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0, 240, 255, 0.18), transparent 65%)",
          filter: "blur(90px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "10%",
          right: "-10%",
          width: 650,
          height: 650,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(157, 0, 255, 0.22), transparent 70%)",
          filter: "blur(100px)",
        }}
      />

      {/* 2. Top Minimalist Luxury Wordmark */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: 60,
          right: 60,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 30,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #00F0FF, #9D00FF)",
              boxShadow: "0 0 15px #00F0FF",
            }}
          />
          <span
            style={{
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              background: "linear-gradient(180deg, #FFFFFF 0%, #A0AEC0 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            KYU PRIVÉ
          </span>
        </div>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: "0.15em",
            color: "rgba(255,255,255,0.4)",
            border: "1px solid rgba(255,255,255,0.15)",
            padding: "6px 16px",
            borderRadius: 999,
            backdropFilter: "blur(12px)",
          }}
        >
          TIER ONE INJECT
        </div>
      </div>

      {/* 3. Floating 3D Character Hero Layer */}
      <div
        style={{
          position: "absolute",
          top: 180,
          left: 0,
          right: 0,
          height: 850,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10,
          transform: `translateY(${floatY}px)`,
        }}
      >
        <Img
          src={staticFile("assets_library/dynamic_renders/valorant_omen_artwork.png")}
          style={{
            height: "100%",
            objectFit: "contain",
            filter: "drop-shadow(0 25px 60px rgba(0, 240, 255, 0.25))",
          }}
        />
      </div>

      {/* 4. Luxury Floating Frosted Glass Slab */}
      <div
        style={{
          position: "absolute",
          bottom: 120,
          left: 50,
          right: 50,
          background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)",
          backdropFilter: "blur(30px)",
          borderRadius: 36,
          border: "1px solid rgba(255,255,255,0.15)",
          padding: "44px 44px",
          boxShadow: "0 40px 100px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.3)",
          overflow: "hidden",
          transform: `translateY(${(1 - cardSpring) * 100}px)`,
          opacity: cardSpring,
          zIndex: 40,
        }}
      >
        {/* Specular Glare Reflection Line */}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${sheenX}%`,
            width: 120,
            background:
              "linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)",
            transform: "skewX(-25deg)",
            pointerEvents: "none",
          }}
        />

        {/* Card Header & Category Tag */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.15em", color: "#00F0FF" }}>
            OFFICIAL DISTRIBUTOR
          </span>
          <span style={{ fontSize: 16, color: "rgba(255,255,255,0.5)" }}>
            INSTANT SYNC • 0.8S
          </span>
        </div>

        {/* Liquid Chrome Title */}
        <h2
          style={{
            fontSize: 54,
            fontWeight: 800,
            lineHeight: 1.1,
            margin: "0 0 16px 0",
            letterSpacing: "-0.03em",
            background: "linear-gradient(180deg, #FFFFFF 0%, #E2E8F0 45%, #94A3B8 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "0 10px 30px rgba(0,0,0,0.5)",
          }}
        >
          Weekly Diamond Pass
        </h2>

        {/* Price & CTA */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 18, color: "rgba(255,255,255,0.4)", textDecoration: "line-through" }}>
              Normal Rp 35.000
            </div>
            <div
              style={{
                fontSize: 64,
                fontWeight: 900,
                letterSpacing: "-0.02em",
                background: "linear-gradient(135deg, #00F0FF 0%, #FFFFFF 60%, #9D00FF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Rp 27.500
            </div>
          </div>

          <div
            style={{
              padding: "18px 36px",
              borderRadius: 20,
              background: "linear-gradient(135deg, rgba(0,240,255,0.2), rgba(157,0,255,0.3))",
              border: "1px solid rgba(0,240,255,0.5)",
              color: "#FFFFFF",
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "0.05em",
              boxShadow: "0 10px 30px rgba(0,240,255,0.3)",
            }}
          >
            ORDER NOW ↗
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
